/**
 * useTeamManagement Hook
 * 
 * Custom hook for team management operations with CRUD + optimistic UI
 * Part of E007-S026: Team Management
 */

import { useState, useCallback } from 'react';
import {
  TeamMember,
  UsersFilters,
  InviteUserRequest,
  InviteUserResponse,
  UpdateUserRequest,
  UserActivity,
} from '../types/team';
import {
  listUsers,
  inviteUser,
  updateUser,
  deactivateUser,
  getUserActivity,
} from '../lib/teamApi';

interface UseTeamManagementReturn {
  // State
  users: TeamMember[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  
  // Actions
  refresh: (filters?: UsersFilters) => Promise<void>;
  invite: (data: InviteUserRequest) => Promise<InviteUserResponse>;
  update: (userId: string, data: UpdateUserRequest) => Promise<void>;
  deactivate: (userId: string) => Promise<void>;
  getActivity: (userId: string, filters?: { start_date?: string; end_date?: string }) => Promise<{ activities: UserActivity[]; total: number }>;
}

export function useTeamManagement(): UseTeamManagementReturn {
  const [users, setUsers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  /**
   * Refresh user list with optional filters
   */
  const refresh = useCallback(async (filters?: UsersFilters) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await listUsers(filters);
      setUsers(response.users);
      setTotalCount(response.metadata.total);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load users';
      setError(errorMessage);
      console.error('Failed to refresh users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Invite a new user (admin only)
   */
  const invite = useCallback(async (data: InviteUserRequest): Promise<InviteUserResponse> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await inviteUser(data);
      
      // Optimistically add the new user to the list
      const newUser: TeamMember = {
        id: response.user_id,
        email: data.email,
        full_name: data.full_name,
        role: data.role,
        status: 'pending',
        last_active: null,
        created_at: new Date().toISOString(),
        account_access: [],
      };
      
      setUsers(prevUsers => [newUser, ...prevUsers]);
      setTotalCount(prev => prev + 1);
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to invite user';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update user role, status, or permissions
   * Uses optimistic UI - updates immediately, rolls back on error
   */
  const update = useCallback(async (userId: string, data: UpdateUserRequest): Promise<void> => {
    setError(null);
    
    // Save previous state for rollback
    const previousUsers = [...users];
    
    // Optimistically update the user in the list
    setUsers(prevUsers =>
      prevUsers.map(user =>
        user.id === userId
          ? {
              ...user,
              ...(data.role && { role: data.role }),
              ...(data.status && { status: data.status }),
            }
          : user
      )
    );
    
    try {
      await updateUser(userId, data);
      // Success - optimistic update already applied
    } catch (err) {
      // Rollback on error
      setUsers(previousUsers);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update user';
      setError(errorMessage);
      throw err;
    }
  }, [users]);

  /**
   * Deactivate a user (soft delete)
   * Uses optimistic UI
   */
  const deactivate = useCallback(async (userId: string): Promise<void> => {
    setError(null);
    
    // Save previous state for rollback
    const previousUsers = [...users];
    
    // Optimistically update status to deactivated
    setUsers(prevUsers =>
      prevUsers.map(user =>
        user.id === userId
          ? {
              ...user,
              status: 'deactivated' as const,
            }
          : user
      )
    );
    
    try {
      await deactivateUser(userId);
      // Success - optimistic update already applied
    } catch (err) {
      // Rollback on error
      setUsers(previousUsers);
      const errorMessage = err instanceof Error ? err.message : 'Failed to deactivate user';
      setError(errorMessage);
      throw err;
    }
  }, [users]);

  /**
   * Get activity log for a specific user
   */
  const getActivity = useCallback(async (
    userId: string,
    filters?: { start_date?: string; end_date?: string }
  ): Promise<{ activities: UserActivity[]; total: number }> => {
    try {
      const response = await getUserActivity(userId, filters);
      return { activities: response.activities, total: response.metadata.total };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load activity log';
      setError(errorMessage);
      throw err;
    }
  }, []);

  return {
    // State
    users,
    loading,
    error,
    totalCount,
    
    // Actions
    refresh,
    invite,
    update,
    deactivate,
    getActivity,
  };
}
